import { createElement, type ReactNode } from "react";
import { cn, tv } from "tailwind-variants";
import { ResponsiveImage } from "#/_packages/umbrella/components/ui/responsive-image";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	Alignment,
	AnimationScrollType,
	AspectRatio,
	HeadingLevel,
	ImageMedia,
	Intent,
	Link,
} from "#/_packages/umbrella/types";
import { getLinkAttributes } from "#/_packages/umbrella/utils/link-attributes";

export type HeroVariant = "standard" | "side" | "background";
export type HeroIntent = Extract<
	Intent,
	"primary" | "secondary" | "light" | "dark" | "none"
>;
export type HeroContentAlignment = Extract<
	Alignment,
	"start" | "center" | "end"
>;
export type HeroHeadingAppearance = `h${HeadingLevel}`;

export type HeroImageMedia = ImageMedia & {
	type: "image";
	src: string;
	alt: string;
	caption?: string;
};

export type HeroExternalVideoMedia = {
	type: "videoExternal";
	src: string;
	title?: string;
	caption?: string;
	autoplay?: boolean;
	loopPlayback?: boolean;
	muteAudio?: boolean;
	hideControls?: boolean;
	enableInteraction?: boolean;
};

export type HeroFileVideoMedia = {
	type: "videoFile";
	src: string;
	title?: string;
	caption?: string;
	autoplay?: boolean;
	loopPlayback?: boolean;
	muteAudio?: boolean;
	hideControls?: boolean;
	enableInteraction?: boolean;
};

export type HeroMedia =
	| HeroImageMedia
	| HeroExternalVideoMedia
	| HeroFileVideoMedia;

export type HeroProps = {
	id?: string;
	variant?: HeroVariant;
	intentColor?: HeroIntent;
	contentAlignment?: HeroContentAlignment;
	imagePositionEnd?: boolean;
	headingLabel?: string | null;
	heading?: string | null;
	headingLevel?: HeadingLevel;
	headingAppearance?: HeroHeadingAppearance;
	preamble?: ReactNode;
	media?: HeroMedia;
	links?: readonly Link[];
	actions?: ReactNode;
	aspectRatio?: AspectRatio;
	animationHoverType?: string[];
	animationScrollType?: AnimationScrollType;
	isDrawer?: boolean;
	className?: string;
};

function toAspectRatioClass(aspectRatio: AspectRatio | undefined) {
	switch (aspectRatio) {
		case "1:1":
			return "aspect-square";
		case "4:3":
			return "aspect-[4/3]";
		case "3:2":
			return "aspect-[3/2]";
		case "16:9":
			return "aspect-video";
		case "21:9":
			return "aspect-[21/9]";
		default:
			return undefined;
	}
}

function toHeadingAppearanceClass(
	headingAppearance: HeroHeadingAppearance | undefined,
) {
	return headingAppearance;
}

function toEmbeddableExternalUrl(src: string) {
	try {
		const parsed = new URL(src);

		if (parsed.hostname.includes("youtube.com")) {
			if (parsed.pathname === "/watch") {
				const id = parsed.searchParams.get("v");
				if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
			}

			if (parsed.pathname.startsWith("/embed/")) {
				return parsed.toString();
			}
		}

		if (parsed.hostname === "youtu.be") {
			const id = parsed.pathname.replace("/", "");
			if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
		}

		if (parsed.hostname.includes("vimeo.com")) {
			if (parsed.hostname.includes("player.vimeo.com")) {
				return parsed.toString();
			}

			const id = parsed.pathname.match(/\/(?:video\/)?(\d+)/)?.[1];
			if (id) return `https://player.vimeo.com/video/${id}`;
		}

		return parsed.toString();
	} catch {
		return src;
	}
}

const hero = tv({
	slots: {
		base: "block__hero",
		content: "hero-content",
		footer: "hero-footer",
	},
	variants: {
		variant: {
			standard: {
				base: "hero-variant-standard",
			},
			side: {
				base: "hero-variant-side",
			},
			background: {
				base: "hero-variant-background",
			},
		},
		intent: {
			primary: { base: "hero-intent-primary" },
			secondary: { base: "hero-intent-secondary" },
			light: { base: "hero-intent-light" },
			dark: { base: "hero-intent-dark" },
			none: { base: "hero-intent-none" },
		},
		contentAlignment: {
			start: { base: "content-alignment-start" },
			center: { base: "content-alignment-center" },
			end: { base: "content-alignment-end" },
		},
		alignmentSelfVertical: {
			stretch: { base: "" },
			start: { base: "self-start" },
			center: { base: "self-center" },
			end: { base: "self-end" },
		},
		animationScrollType: {
			none: { base: "" },
			"anim-view-fadeIn-moveUp": { base: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { base: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { base: "anim-view-fadeIn-moveLeft" },
		},
	},
	defaultVariants: {
		variant: "standard",
		intent: "none",
		contentAlignment: "start",
		alignmentSelfVertical: "stretch",
		animationScrollType: "none",
	},
});

function HeroMediaContent({
	media,
	variant,
	aspectRatio,
	hoverMediaClasses,
}: {
	media: HeroMedia;
	variant: HeroVariant;
	aspectRatio?: AspectRatio;
	hoverMediaClasses?: string | null;
}) {
	const isBackground = variant === "background";
	const isSide = variant === "side";
	const aspectRatioClass = !isBackground ? toAspectRatioClass(aspectRatio) : "";
	const figureClass = cn(
		"figure",
		"w-full",
		isSide && "relative h-md-100",
		aspectRatioClass,
		hoverMediaClasses,
	);
	const mediaClass = cn(
		"hero-img",
		!isBackground && aspectRatioClass && "h-full w-full object-cover",
	);

	if (media.type === "image") {
		return (
			<figure className={figureClass}>
				<ResponsiveImage image={media} className={mediaClass} />
				{media.caption ? (
					<figcaption className="figure-caption">{media.caption}</figcaption>
				) : null}
			</figure>
		);
	}

	if (media.type === "videoExternal") {
		return (
			<figure className={figureClass}>
				<iframe
					title={media.title ?? "Embedded hero video"}
					src={toEmbeddableExternalUrl(media.src)}
					className="hero-video"
					loading="lazy"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
					{...(!media.enableInteraction && {
						onContextMenu: (event) => event.preventDefault(),
					})}
				/>
				{media.caption ? (
					<figcaption className="figure-caption">{media.caption}</figcaption>
				) : null}
			</figure>
		);
	}

	return (
		<figure className={figureClass}>
			<video
				className="hero-video"
				controls={!media.hideControls}
				autoPlay={media.autoplay}
				loop={media.loopPlayback}
				muted={media.muteAudio}
				playsInline={media.autoplay}
				{...(!media.enableInteraction && {
					onContextMenu: (event) => event.preventDefault(),
				})}
			>
				<source src={media.src} />
			</video>
			{media.caption ? (
				<figcaption className="figure-caption">{media.caption}</figcaption>
			) : null}
		</figure>
	);
}

function HeroContent({
	headingLabel,
	heading,
	headingLevel,
	headingAppearance,
	preamble,
	links,
	actions,
	footerClassName,
}: {
	headingLabel?: string | null;
	heading?: string | null;
	headingLevel: HeadingLevel;
	headingAppearance?: HeroHeadingAppearance;
	preamble?: ReactNode;
	links: readonly Link[];
	actions?: ReactNode;
	footerClassName: string;
}) {
	const headingAppearanceClass = toHeadingAppearanceClass(headingAppearance);
	const headingElement = heading
		? createElement(
				`h${headingLevel}`,
				{ className: cn("hero-title", headingAppearanceClass) },
				heading,
			)
		: null;

	return (
		<>
			{headingLabel || heading ? (
				<header>
					{headingLabel ? (
						<span className="hero-kicker kicker">{headingLabel}</span>
					) : null}
					{headingElement}
				</header>
			) : null}
			{preamble ? <div className="hero-body mb-0-last">{preamble}</div> : null}
			{actions ? <div className={footerClassName}>{actions}</div> : null}
			{!actions && links.length > 0 ? (
				<div className={footerClassName}>
					{links.map((link) => (
						<a
							key={link.id}
							href={link.href}
							target={link.target}
							{...getLinkAttributes(link)}
							title={link.title}
							className="btn btn-link"
						>
							{link.label}
						</a>
					))}
				</div>
			) : null}
		</>
	);
}

export function Hero({
	id,
	variant = "standard",
	intentColor = "none",
	contentAlignment = "start",
	imagePositionEnd = false,
	headingLabel,
	heading,
	headingLevel = 2,
	headingAppearance,
	preamble,
	media,
	links = [],
	actions,
	aspectRatio = "none",
	animationHoverType = [],
	animationScrollType = "none",
	className,
}: HeroProps) {
	const { base, content, footer } = hero({
		variant,
		intent: intentColor,
		contentAlignment,
		animationScrollType,
	});
	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);

	if (variant === "side") {
		return (
			<div id={id} className={cn(base(), hoverItemClasses, className)}>
				<div
					className={cn(
						"hero-inner flex flex-col md:flex-row",
						imagePositionEnd && "flex-col-reverse md:flex-row-reverse",
					)}
				>
					{media?.src ? (
						<div className="md:w-1/2">
							<HeroMediaContent
								media={media}
								variant={variant}
								aspectRatio={aspectRatio}
								hoverMediaClasses={hoverMediaClasses}
							/>
						</div>
					) : null}
					<div className={cn("flex items-center", media?.src && "md:w-1/2")}>
						<div className={content()}>
							<HeroContent
								headingLabel={headingLabel}
								heading={heading}
								headingLevel={headingLevel}
								headingAppearance={headingAppearance}
								preamble={preamble}
								links={links}
								actions={actions}
								footerClassName={footer()}
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (variant === "background") {
		return (
			<div id={id} className={cn(base(), hoverItemClasses, className)}>
				<div className={cn("w-full", media?.src && "has-image")}>
					<div className="container">
						{media ? (
							<HeroMediaContent
								media={media}
								variant={variant}
								aspectRatio={aspectRatio}
								hoverMediaClasses={hoverMediaClasses}
							/>
						) : null}
						<div className="flex justify-center">
							<div className="z-[1] px-[var(--umb-spacer-6)] py-[var(--umb-spacer-9)] md:px-[var(--umb-spacer-8)] lg:w-9/12 lg:px-0 xl:w-1/2 xl:py-[var(--umb-spacer-13)]">
								<div className={content()}>
									<HeroContent
										headingLabel={headingLabel}
										heading={heading}
										headingLevel={headingLevel}
										headingAppearance={headingAppearance}
										preamble={preamble}
										links={links}
										actions={actions}
										footerClassName={footer()}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div id={id} className={cn(base(), hoverItemClasses, className)}>
			<div className="hero-inner hero-variant-standard">
				<div className="flex justify-center">
					<div className="lg:w-8/12 xl:w-7/12">
						<div
							className={cn(
								"flex flex-col gap-y-[var(--umb-spacer-6)] md:gap-y-[var(--umb-spacer-9)]",
								imagePositionEnd && "flex-col-reverse",
							)}
						>
							{media ? (
								<HeroMediaContent
									media={media}
									variant={variant}
									aspectRatio={aspectRatio}
									hoverMediaClasses={hoverMediaClasses}
								/>
							) : null}
							<div className={content()}>
								<HeroContent
									headingLabel={headingLabel}
									heading={heading}
									headingLevel={headingLevel}
									headingAppearance={headingAppearance}
									preamble={preamble}
									links={links}
									actions={actions}
									footerClassName={footer()}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Hero;
